import { CheckCircle2, CreditCard, LoaderCircle, Lock, ShieldCheck, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";
import { resolvePackageIdFromPlan, resolvePublicPlanFromPackage, resolveSelectedPlan } from "@/lib/onboardingPlans";
import { getReferralDisplayState } from "@/lib/referrals";
import { assertRootDomainOnly } from "@/lib/routeIsolation";

export const CheckoutPage = ({ resolveUserDashboardPath, user }) => {
  const navigate = useNavigate();
  const { isAgentPath } = useSiteBranding();
  const isRootDomainAllowed = assertRootDomainOnly(isAgentPath);
  const [searchParams] = useSearchParams();
  const requestedPlan = resolvePackageIdFromPlan(searchParams.get("plan") || resolvePublicPlanFromPackage(user?.plan_type));
  const selectedPlan = resolveSelectedPlan(requestedPlan);
  const cancelledCheckout = searchParams.get("billing_status") === "cancelled";
  const referralState = getReferralDisplayState(searchParams.get("ref"));
  const referralCode = referralState.activeCode;
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Checkout | SG Prop Portal",
    description: "Confirm your SG Prop Portal subscription and activate your Signature workspace.",
  });

  const hasCheckoutAccess = useMemo(() => {
    if (!user) return false;
    if (user.role === "agent" && user.subscription_status === "Active") return true;
    return ["agent", "pending_agent"].includes(user.role) && user.onboarding_state === "payment_required";
  }, [user]);

  if (!isRootDomainAllowed) {
    return <Navigate replace to="/" />;
  }

  if (user?.role === "agent" && user.subscription_status === "Active") {
    return <Navigate replace to={resolveUserDashboardPath(user)} />;
  }

  if (!hasCheckoutAccess) {
    return <Navigate replace to={`/signup?plan=${selectedPlan.publicPlanId}`} />;
  }

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await api.post(
        "/billing/checkout-session",
        { package_id: selectedPlan.packageId, origin_url: window.location.origin, onboarding_flow: true, referral_code: referralCode },
        authConfig(),
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to start your Stripe checkout"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.16),transparent_24%),linear-gradient(180deg,#fffaf0_0%,#f9f2e6_52%,#f6ecda_100%)] px-6 py-10 text-[#001935]" data-testid="checkout-page">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-[30px] border border-[#001935]/10 bg-[#FFFAF1] p-6 shadow-[0_30px_90px_rgba(0,25,53,0.1)] md:p-8" data-testid="checkout-shell">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]">Step 3 — Subscription Payment</p>
              <h1 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]" data-testid="checkout-heading">Confirm your plan and activate the workspace.</h1>
            </div>
            <button className="outline-button border-[#D4AF37]/40 text-[#001935] hover:bg-[#fff4d8]" data-testid="checkout-back-button" onClick={() => navigate(`/signup?plan=${selectedPlan.publicPlanId}${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`)} type="button">
              Back to Signup
            </button>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#fff4dd] px-4 py-2 text-sm font-semibold text-[#001935]" data-testid="checkout-security-chip">
            <Lock className="h-4 w-4 text-[#D4AF37]" /> Institutional Stripe Security Lock
          </div>

          {referralCode ? (
            <div className="mt-4 rounded-[22px] border border-[#001935]/10 bg-white px-5 py-4 text-sm font-semibold text-[#001935]" data-testid="checkout-referral-banner">
              <p data-testid="checkout-referral-banner-primary">
                {referralState.isLockedToDifferentCode
                  ? <>This browser is already locked to referral code <span className="text-[#8b6d16]">{referralCode}</span> for 60 days.</>
                  : referralState.isStoredWithoutQuery
                    ? <>This browser currently has first-click referral attribution locked for 60 days: <span className="text-[#8b6d16]">{referralCode}</span></>
                    : <>Referral attribution is locked on first click for 60 days. Current code: <span className="text-[#8b6d16]">{referralCode}</span></>}
              </p>
              {referralState.isLockedToDifferentCode ? (
                <p className="mt-2 text-xs font-medium leading-6 text-[#5e6e82]" data-testid="checkout-referral-banner-secondary">
                  This link used <span className="font-semibold text-[#8b6d16]">{referralState.incomingCode}</span>, but it will not override the existing lock in this browser.
                </p>
              ) : null}
            </div>
          ) : null}

          {cancelledCheckout ? (
            <div className="mt-6 rounded-[24px] border border-[#D4AF37]/20 bg-[#fff3da] px-5 py-4 text-sm leading-7 text-[#001935]" data-testid="checkout-cancelled-banner">
              Payment was not completed. You’re still on the secure activation step.
            </div>
          ) : null}

          <div className="mt-6 grid gap-3 md:grid-cols-4">
            {[
              { number: "01", label: "Account Created", active: true },
              { number: "02", label: "CEA Verified", active: true },
              { number: "03", label: "Subscription Payment", active: true },
              { number: "04", label: "Workspace Setup Wizard", active: false },
            ].map((item) => (
              <div className={`rounded-[22px] border px-4 py-4 ${item.active ? "border-[#D4AF37] bg-[#fff4dd]" : "border-[#001935]/10 bg-[#f5efe2]"}`} data-testid={`checkout-step-${item.number}`} key={item.number}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Step {item.number}</p>
                <p className="mt-2 text-lg font-semibold text-[#001935]">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="space-y-5 rounded-[28px] border border-[#001935]/10 bg-[#f4ecdf] p-6" data-testid="checkout-summary-card">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Selected plan</p>
              <div className="inline-flex items-center rounded-full border border-[#D4AF37]/30 bg-[#fff4dd] px-4 py-2 text-sm font-semibold text-[#001935]" data-testid={`checkout-plan-selected-${selectedPlan.publicPlanId}`}>
                {selectedPlan.title}
              </div>
              <h2 className="font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]">{selectedPlan.price}</h2>
              <p className="text-sm leading-8 text-[#42566f]">{selectedPlan.body}</p>
              <div className="rounded-[24px] border border-[#001935]/10 bg-[#fff8ee] p-4" data-testid="checkout-compliance-card">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]">Included immediately</p>
                <div className="mt-3 space-y-2 text-sm leading-7 text-[#42566f]">
                  <p>• 2026 PMLPFTF tracker included</p>
                  <p>• Hosted Stripe activation route with recurring subscription controls</p>
                  <p>• Referral attribution is written into the checkout metadata when present</p>
                </div>
              </div>
            </div>

            <div className="space-y-5 rounded-[28px] border border-[#001935]/10 bg-[#f4ecdf] p-6" data-testid="checkout-payment-card">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Final gatekeeper</p>
                <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]">Activation happens only after payment confirms.</h2>
                <p className="mt-3 text-sm leading-8 text-[#42566f]">This launch flow skips the free trial. Successful payment simulation is the final gate before we provision the live agent workspace.</p>
              </div>

              <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="checkout-readiness-card">
                <div className="space-y-3 text-sm leading-7 text-[#001935]">
                  <p className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#D4AF37]" /> Root-domain account created</p>
                  <p className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Simulated CEA verification completed</p>
                  <p className="inline-flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#D4AF37]" /> Plan-specific Stripe simulation ready</p>
                  <p className="inline-flex items-center gap-2"><Lock className="h-4 w-4 text-[#D4AF37]" /> Secure activation handoff in progress</p>
                </div>
              </div>

              <button className="gold-button !w-full !justify-center" data-testid="checkout-complete-button" disabled={loading} onClick={handleCheckout} type="button">
                {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Complete My Signature Portal
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};